import { useState } from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { Button } from '@/components/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/base/card';
import { Input } from '@/components/base/input';
import { Label } from '@/components/base/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/base/alert';
import { Footer } from '@/components/Footer';
import { decodeKey } from '@/localSign';
import { useAccount } from '@/state/account';

export function ImportForm() {
  const { setAccount } = useAccount();
  const [input, setInput] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canImport = input.trim().length > 0 && confirmed && !busy;

  const handleImport = () => {
    if (!canImport) return;
    setError(null);
    setBusy(true);
    try {
      const decoded = decodeKey(input);
      setAccount({ scalar: decoded.scalar, pubkey: decoded.pubkey, format: decoded.format });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to import private key.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="para:flex para:min-h-screen para:flex-col para:bg-muted para:px-4 para:py-8">
      <header className="para:flex para:flex-col para:items-center para:gap-2 para:text-center">
        <span className="para:inline-flex para:items-center para:rounded-full para:border para:border-border para:bg-card para:px-3 para:py-0.5 para:text-xs para:font-medium para:text-muted-foreground">
          Open Source
        </span>
        <h1 className="para:text-4xl para:font-semibold">Solana Wallet Recovery</h1>
        <p className="para:text-sm para:text-muted-foreground">
          Import a private key exported from Para to view balances and move funds.
        </p>
      </header>
      <div className="para:flex para:flex-1 para:items-center para:justify-center para:py-6">
        <Card className="para:w-full para:max-w-xl">
          <CardHeader>
            <CardTitle className="para:flex para:items-center para:gap-2 para:text-2xl">
              <KeyRound className="para:size-6 para:text-primary" />
              Import Solana private key
            </CardTitle>
            <CardDescription>
              Paste the Solana private key that you exported from Para into the field below. The key is held in this
              browser tab only — never sent to any server, stored in your browser, or shared with Para.
            </CardDescription>
          </CardHeader>
          <CardContent className="para:flex para:flex-col para:gap-4">
            <div className="para:flex para:flex-col para:gap-2">
              <Label htmlFor="private-key">Private key</Label>
              <div className="para:relative">
                <Input
                  id="private-key"
                  type={showKey ? 'text' : 'password'}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="hex or base58"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleImport();
                  }}
                  className="para:pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  aria-label={showKey ? 'Hide private key' : 'Show private key'}
                  title={showKey ? 'Hide private key' : 'Show private key'}
                  className="para:absolute para:right-0 para:top-0 para:flex para:h-11 para:w-11 para:items-center para:justify-center para:text-muted-foreground para:hover:text-foreground"
                >
                  {showKey ? <EyeOff className="para:size-4" /> : <Eye className="para:size-4" />}
                </button>
              </div>
            </div>
            <Alert variant="destructive">
              <AlertTitle>Anyone with this key can drain your wallet.</AlertTitle>
              <AlertDescription>
                <p className="para:text-muted-foreground">
                  Only paste your private key on a device you trust, on a network you trust, with no one looking at
                  your screen. After you sweep your funds, treat the key as compromised.
                </p>
                <label className="para:mt-2 para:flex para:items-start para:gap-2 para:text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="para:mt-0.5 para:size-4 para:accent-destructive"
                  />
                  <span>I understand the security implications and want to continue.</span>
                </label>
              </AlertDescription>
            </Alert>
            {error && (
              <Alert variant="destructive">
                <AlertTitle>Could not import</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button onClick={handleImport} isLoading={busy} disabled={!canImport}>
              Import
            </Button>
          </CardContent>
        </Card>
      </div>
      <div className="para:mx-auto para:w-full para:max-w-xl">
        <Footer />
      </div>
    </div>
  );
}
